/**
 * StripeSync.jsx – Phase 1a: Synchronisation & Verbindungsstatus (Read-Only)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Loader2, CheckCircle2, XCircle, RefreshCw, Wifi, WifiOff,
  AlertTriangle, Clock, Webhook, Activity,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function InfoRow({ label, value, valueClass = '' }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-medium ${valueClass}`}>{value || '–'}</span>
    </div>
  );
}

function EventBadge({ status }) {
  const c = {
    processed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    pending: 'bg-amber-100 text-amber-700',
  }[status] || 'bg-gray-100 text-gray-600';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c}`}>{status}</span>;
}

export default function StripeSync() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/superadmin/stripe-cc/sync/status`, { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fehler beim Laden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const d = data || {};

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Stripe-Verbindung & Synchronisation</h2>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5" data-testid="stripe-sync-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 rounded-xl text-sm text-red-700">
          <XCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : data && (
        <>
          {/* Verbindungsstatus Card */}
          <div className={`rounded-xl border p-5 space-y-4 ${
            d.stripe_connected
              ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
          }`}>
            <div className="flex items-center gap-3">
              {d.stripe_connected
                ? <Wifi className="h-6 w-6 text-green-600" />
                : <WifiOff className="h-6 w-6 text-red-600" />}
              <div>
                <p className="font-semibold text-base">
                  {d.stripe_connected ? 'Stripe verbunden' : 'Stripe nicht verbunden'}
                </p>
                {d.stripe_connected && (
                  <p className="text-xs text-muted-foreground">Letzte Prüfung: {d.last_api_check}</p>
                )}
                {d.error && <p className="text-xs text-red-600 mt-0.5">{d.error}</p>}
              </div>
            </div>

            <div className="space-y-0">
              <InfoRow
                label="Modus"
                value={d.mode_label}
                valueClass={d.mode === 'live' ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300'}
              />
              <InfoRow
                label="Legacy-Webhook"
                value={d.webhook_secret_configured ? '✓ Konfiguriert' : '✗ Nicht konfiguriert'}
                valueClass={d.webhook_secret_configured ? 'text-green-600' : 'text-red-600'}
              />
              <InfoRow
                label="Legacy-Webhook-Verarbeitung"
                value="Aktiv (verarbeitet Zahlungsevents)"
                valueClass="text-green-600"
              />
            </div>
          </div>

          {/* Modus-Warnung */}
          {d.mode === 'test' && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-amber-800 dark:text-amber-300">TESTMODUS AKTIV</p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  Diese Umgebung verwendet Stripe Test-Schlüssel. Alle Daten sind Testdaten.
                  Keine echten Zahlungen werden verarbeitet.
                </p>
              </div>
            </div>
          )}

          {/* Legacy Webhook – läuft, aber nicht in CC protokolliert */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Webhook className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Webhook-Konfiguration</h3>
            </div>
            <div className="space-y-0">
              <InfoRow label="Legacy-Webhook" value="Konfiguriert ✓" valueClass="text-green-600" />
              <InfoRow label="Legacy-Webhook-Verarbeitung" value="Aktiv – verarbeitet Zahlungs- und Abonnement-Events" valueClass="text-green-600" />
              <InfoRow label="Legacy-Webhook-Endpunkt" value="/stripe/webhook" />
            </div>
          </div>

          {/* Neue CC Ereignisprotokollierung – noch nicht aktiv */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Stripe Control Center – Ereignisprotokollierung</h3>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700 rounded-lg">
              <Clock className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Protokollierung noch nicht aktiv</p>
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  Das Stripe Control Center protokolliert Webhook-Events noch nicht eigenständig.
                  Die Ereignis-Datenbank wird mit Phase 1b aktiviert.
                </p>
              </div>
            </div>
            <div className="space-y-0">
              <InfoRow label="Events in neuer Ereignis-DB" value="0 (Protokollierung nicht aktiv)" />
              <InfoRow label="Fehlgeschlagene Events (CC)" value="Keine Auswertung möglich" />
              <InfoRow label="Letzter protokollierter Event" value="–" />
            </div>
          </div>

          {/* Letzte Events */}
          {d.recent_events?.length > 0 && (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">Letzte Events</h3>
              </div>
              <div className="space-y-2">
                {d.recent_events.map((ev, i) => (
                  <div key={ev.event_id || i} className="flex items-center justify-between gap-2 p-2.5 bg-gray-50 dark:bg-gray-800/40 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-mono truncate text-muted-foreground">{ev.event_type}</p>
                      <p className="text-xs text-muted-foreground">{ev.received_at}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {ev.livemode === false && <span className="text-xs text-amber-600">Test</span>}
                      <EventBadge status={ev.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bestehende Stripe-Sync-Funktion verlinken */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700 rounded-xl">
            <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Manueller Stripe-Sync verfügbar</p>
              <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                Der vollständige Stripe-Sync (Zahlungsmethoden, Rechnungen, Abo-Status) ist in der 
                Kundenliste unter "Stripe Komplett-Sync" verfügbar.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
